import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PortalPage, Field, Section } from "../../components/portal/PortalPage";
import { usePortal } from "../../components/portal/usePortal";
import { fmtDate, TIER_LABEL, TIER_DESC } from "../../components/portal/portalUtils";
import { UserPlus, PaperPlaneTilt, Trash, ShieldCheck, User, Eye } from "@phosphor-icons/react";
import { CardListSkeleton } from "../../components/Skeletons";
import { toast } from "sonner";

const TIER_ICON = { client_admin: ShieldCheck, client_user: User, client_viewer: Eye };

export default function ClientTeamPage() {
  const { perms, user } = usePortal();
  const [members, setMembers] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", tier: "client_user" });
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/client-hub/team").then(({ data }) => setMembers(data.members || [])).catch(() => setMembers([]));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!f.name.trim() || !f.email.trim()) return toast.error("Name and email are required");
    setBusy(true);
    try { await API.post("/client-hub/team/invite", f); toast.success(`Invitation sent to ${f.email}`); setInviting(false); setF({ name: "", email: "", phone: "", tier: "client_user" }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const update = async (id, patch) => { try { await API.put(`/client-hub/team/${id}`, patch); toast.success("Updated"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const resend = async (id) => { try { const { data } = await API.post(`/client-hub/team/${id}/resend`); toast.success(data.message); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const remove = async (m) => { if (!window.confirm(`Remove ${m.name} from the portal?`)) return; try { await API.delete(`/client-hub/team/${m.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };

  return (
    <PortalPage
      title="Team"
      subtitle="Give colleagues their own login. Administrators approve quotes and manage the team; users raise work; viewers just watch."
      testid="client-team"
      actions={perms.can_manage_team && <Button size="sm" onClick={() => setInviting(true)} data-testid="invite-member-btn"><UserPlus size={14} className="mr-1" /> Invite Colleague</Button>}
    >
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        {Object.entries(TIER_LABEL).map(([k, l]) => { const Icon = TIER_ICON[k]; return (
          <div key={k} className="border border-border rounded-md p-3 bg-card"><p className="text-xs font-semibold flex items-center gap-1.5"><Icon size={14} className="text-primary" /> {l}</p><p className="text-[11px] text-muted-foreground mt-1">{TIER_DESC[k]}</p></div>
        ); })}
      </div>
      <Section title={`Members (${members?.length ?? 0})`} testid="team-list">
        {members === null ? <CardListSkeleton rows={3} showHeader={false} /> : (
          <ul className="divide-y divide-border -mx-4 -my-4">
            {members.map((m) => {
              const Icon = TIER_ICON[m.tier] || User;
              const isMe = m.id === user?.id;
              return (
                <li key={m.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3" data-testid={`member-${m.id}`}>
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${m.active === false ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"}`}><Icon size={18} weight="duotone" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{m.name} {isMe && <span className="text-[10px] text-muted-foreground">(you)</span>}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {m.invite_status === "pending" ? <Badge variant="secondary" className="text-[9px]">Invite pending</Badge> : m.active === false ? <Badge variant="destructive" className="text-[9px]">Deactivated</Badge> : <Badge className="text-[9px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>}
                      <span className="text-[10px] text-muted-foreground">Added {fmtDate(m.created_at)}{m.invited_by_name ? ` by ${m.invited_by_name}` : ""}</span>
                    </div>
                  </div>
                  {perms.can_manage_team ? (
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <Select value={m.tier} onValueChange={(v) => update(m.id, { tier: v })} disabled={isMe}>
                        <SelectTrigger className="h-8 w-40 text-xs" data-testid={`member-tier-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(TIER_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                      {m.invite_status === "pending" && <Button size="sm" variant="outline" className="h-8" onClick={() => resend(m.id)} data-testid={`member-resend-${m.id}`}><PaperPlaneTilt size={13} className="mr-1" /> Resend</Button>}
                      {!isMe && m.invite_status !== "pending" && <Button size="sm" variant="outline" className="h-8" onClick={() => update(m.id, { active: m.active === false })} data-testid={`member-toggle-${m.id}`}>{m.active === false ? "Reactivate" : "Deactivate"}</Button>}
                      {!isMe && <Button size="sm" variant="ghost" className="h-8 text-red-600" onClick={() => remove(m)} data-testid={`member-remove-${m.id}`}><Trash size={14} /></Button>}
                    </div>
                  ) : <Badge variant="outline" className="text-[10px]">{TIER_LABEL[m.tier]}</Badge>}
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Dialog open={inviting} onOpenChange={setInviting}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Invite a colleague</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Full name *"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name" /></Field>
            <Field label="Email *"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email" /></Field>
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="invite-phone" /></Field>
            <Field label="Portal role" hint={TIER_DESC[f.tier]}>
              <Select value={f.tier} onValueChange={(v) => setF({ ...f, tier: v })}><SelectTrigger data-testid="invite-tier"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(TIER_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
            </Field>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setInviting(false)}>Cancel</Button><Button onClick={invite} disabled={busy} data-testid="invite-send">Send Invitation</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalPage>
  );
}
